'use client';

import { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { useTranslations } from 'use-intl';

import type { ResponderPanel } from '@/shared-survey';
import { type Channel, type GetProjectQuery } from '@/survey-graphql';
import { toast } from '@/ui';

import type { SurveyAiFormsAudio } from './survey-ai-forms-audio.hooks';
import type { AiFormsResponseValues } from './survey-ai-forms-form.hooks';
import { type MatrixAnswer } from './survey-ai-forms-matrix.types';
import { useSurveyAiFormsSubmissionAudio } from './survey-ai-forms-submission-audio.hooks';
import { useSurveyAiFormsSubmissionFinal } from './survey-ai-forms-submission-final.hooks';
import { useSurveyAiFormsSubmissionInsight } from './survey-ai-forms-submission-insight.hooks';
import { useSurveyAiFormsSubmissionMessage } from './survey-ai-forms-submission-message.hooks';
import {
  areAllRequiredQuestionsAnswered,
  areAllRequiredQuestionsSubmittedAndAnswered,
  buildSubQuestionAnswers,
  formatValueForMessage,
  getAnsweredResponsesKey,
  getQuestionValueAsMatrix,
  isMatrixQuestion,
  isValidResponseValue,
  pruneClearedRequiredSubmissions,
} from './survey-ai-forms.helpers';
import type { QuestionResponse, ViewMode } from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface UseSurveyAiFormsSubmissionProps {
  project: NonNullable<GetProjectQuery['project']>;
  questions: ProjectQuestion[];
  projectId: string;
  form: UseFormReturn<AiFormsResponseValues>;
  sessionId: string;
  isPreviewMode: boolean;
  viewMode: ViewMode;
  currentQuestionId: string | null;
  setCurrentQuestionId: (id: string | null) => void;
  responses: Map<string, QuestionResponse>;
  transcripts: Record<string, string | undefined>;
  audio: SurveyAiFormsAudio;
  channel?: Channel;
  panel?: ResponderPanel | null;
}

export function useSurveyAiFormsSubmission({
  project,
  questions,
  projectId,
  form,
  sessionId,
  isPreviewMode,
  viewMode,
  currentQuestionId,
  setCurrentQuestionId,
  responses,
  transcripts,
  audio,
  channel,
  panel = null,
}: UseSurveyAiFormsSubmissionProps) {
  const t = useTranslations('AIForms.toast');
  const tCommon = useTranslations('AIForms');
  const { setValue } = form;

  const currentQuestion =
    questions.find((q) => q.id === currentQuestionId) ?? null;

  const { insightId, ensureInsightId, isAddingInsight } =
    useSurveyAiFormsSubmissionInsight({
      projectId,
      sessionId,
      isPreviewMode,
      channel,
      panel,
    });

  const {
    isUploadingAudio,
    stopListeningAndPrepareAudio,
    formatTranscriptAndCreateAudioAsset,
    cleanupAudioAfterSubmit,
  } = useSurveyAiFormsSubmissionAudio({
    audio,
    isPreviewMode,
  });

  const {
    submittedQuestionIds,
    setSubmittedQuestionIds,
    isAddingMessage,
    handleAddFormMessage,
    moveToNextQuestion,
  } = useSurveyAiFormsSubmissionMessage({
    projectId,
    sessionId,
    isPreviewMode,
    questions,
    currentQuestionId,
    setCurrentQuestionId,
  });

  const responsesKey = getAnsweredResponsesKey(responses);
  const [prevResponsesKey, setPrevResponsesKey] = useState(responsesKey);
  if (prevResponsesKey !== responsesKey) {
    setPrevResponsesKey(responsesKey);
    setSubmittedQuestionIds((prev) =>
      pruneClearedRequiredSubmissions(questions, prev, responses)
    );
  }

  const {
    validationErrors,
    hasTriedSubmit,
    isSubmitSuccessful,
    isSubmitting,
    isBatchingMessagesLoading,
    handleSubmit,
    handleFormSubmit,
  } = useSurveyAiFormsSubmissionFinal({
    project,
    questions,
    projectId,
    form,
    sessionId,
    isPreviewMode,
    responses,
    transcripts,
    submittedQuestionIds,
    setSubmittedQuestionIds,
    setCurrentQuestionId,
    insightId,
    ensureInsightId,
    panel,
  });

  const handleNext = async () => {
    if (currentQuestionId) {
      try {
        const currentInsightId = isPreviewMode ? null : await ensureInsightId();

        if (!isPreviewMode && !currentInsightId) {
          toast.add({
            title: t('genericError'),
            type: 'error',
          });
          return;
        }

        const currentResponse = responses.get(currentQuestionId);
        const question = questions.find((q) => q.id === currentQuestionId);
        if (currentResponse && isValidResponseValue(currentResponse.value)) {
          const { wasInVoiceMode, startDate } = stopListeningAndPrepareAudio();

          const { assets, formattedTranscript } =
            await formatTranscriptAndCreateAudioAsset(
              wasInVoiceMode,
              startDate,
              question,
              transcripts[currentQuestionId]
            );

          const matrixValue =
            question && isMatrixQuestion(question)
              ? getQuestionValueAsMatrix(currentResponse.value)
              : undefined;
          const subQuestionAnswers = matrixValue
            ? buildSubQuestionAnswers(matrixValue)
            : undefined;

          const updatedSubmittedIds = await handleAddFormMessage({
            content: formatValueForMessage(
              currentResponse.value,
              question,
              tCommon('summary.notApplicable'),
              tCommon('summary.noAnswer')
            ),
            insightId: currentInsightId,
            questionId: currentQuestionId,
            sessionId,
            transcript: formattedTranscript,
            assets,
            subQuestionAnswers,
          });

          if (updatedSubmittedIds) {
            cleanupAudioAfterSubmit(wasInVoiceMode);
            moveToNextQuestion(updatedSubmittedIds);
          }
          return;
        }
      } catch (error: unknown) {
        toast.add({
          title: error instanceof Error ? error.message : t('genericError'),
          type: 'error',
        });
        return;
      }
    }
  };

  const handleAiResponseSubmit = async (
    value: string | number | string[] | MatrixAnswer
  ) => {
    if (!currentQuestion) return;

    setValue(`answers.${currentQuestion.id}`, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    try {
      const currentInsightId = isPreviewMode ? null : await ensureInsightId();

      if (!isPreviewMode && !currentInsightId) {
        toast.add({
          title: t('genericError'),
          type: 'error',
        });
        return;
      }

      const { wasInVoiceMode, startDate } = stopListeningAndPrepareAudio();

      const { assets, formattedTranscript } =
        await formatTranscriptAndCreateAudioAsset(
          wasInVoiceMode,
          startDate,
          currentQuestion,
          transcripts[currentQuestion.id]
        );

      const matrixValue = isMatrixQuestion(currentQuestion)
        ? getQuestionValueAsMatrix(value)
        : undefined;
      const subQuestionAnswers = matrixValue
        ? buildSubQuestionAnswers(matrixValue)
        : undefined;

      const updatedSubmittedIds = await handleAddFormMessage({
        content: formatValueForMessage(
          value,
          currentQuestion,
          tCommon('summary.notApplicable'),
          tCommon('summary.noAnswer')
        ),
        insightId: currentInsightId,
        questionId: currentQuestion.id,
        sessionId,
        transcript: formattedTranscript,
        assets,
        subQuestionAnswers,
      });

      if (updatedSubmittedIds) {
        cleanupAudioAfterSubmit(wasInVoiceMode);
        moveToNextQuestion(updatedSubmittedIds);
      }
    } catch (error: unknown) {
      toast.add({
        title: error instanceof Error ? error.message : t('genericError'),
        type: 'error',
      });
    }
  };

  const handleFormResponseChange = (
    questionId: string,
    value: string | string[] | number | MatrixAnswer
  ) => {
    setValue(`answers.${questionId}`, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  const handleFormResponseSubmit = async (
    value: string | number | string[] | MatrixAnswer
  ) => {
    if (!currentQuestionId) return;

    try {
      const currentInsightId = isPreviewMode ? null : await ensureInsightId();

      if (!isPreviewMode && !currentInsightId) {
        toast.add({
          title: t('genericError'),
          type: 'error',
        });
        return;
      }

      const question = questions.find((q) => q.id === currentQuestionId);

      const { wasInVoiceMode, startDate } = stopListeningAndPrepareAudio();

      const { assets, formattedTranscript } =
        await formatTranscriptAndCreateAudioAsset(
          wasInVoiceMode,
          startDate,
          question,
          transcripts[currentQuestionId]
        );

      const matrixValue =
        question && isMatrixQuestion(question)
          ? getQuestionValueAsMatrix(value)
          : undefined;
      const subQuestionAnswers = matrixValue
        ? buildSubQuestionAnswers(matrixValue)
        : undefined;

      const updatedSubmittedIds = await handleAddFormMessage({
        content: formatValueForMessage(
          value,
          question,
          tCommon('summary.notApplicable'),
          tCommon('summary.noAnswer')
        ),
        insightId: currentInsightId,
        questionId: currentQuestionId,
        sessionId,
        transcript: formattedTranscript,
        assets,
        subQuestionAnswers,
      });

      if (updatedSubmittedIds) {
        cleanupAudioAfterSubmit(wasInVoiceMode);
        moveToNextQuestion(updatedSubmittedIds);
      }
    } catch (error: unknown) {
      toast.add({
        title: error instanceof Error ? error.message : t('genericError'),
        type: 'error',
      });
    }
  };

  const canSubmitAi = isPreviewMode
    ? areAllRequiredQuestionsAnswered(questions, responses) &&
      !isSubmitSuccessful
    : areAllRequiredQuestionsSubmittedAndAnswered(
        questions,
        submittedQuestionIds,
        responses
      ) &&
      insightId !== null &&
      !isSubmitSuccessful;

  const canSubmitForm = !isSubmitSuccessful;
  const canSubmit = viewMode === 'form' ? canSubmitForm : canSubmitAi;

  const isProcessingResponse =
    isAddingInsight || isAddingMessage || isUploadingAudio;

  const isSubmittingForButton =
    viewMode === 'form'
      ? (isAddingInsight || isBatchingMessagesLoading || isSubmitting) &&
        !isSubmitSuccessful
      : isSubmitting && !isSubmitSuccessful;

  return {
    insightId,
    submittedQuestionIds,
    validationErrors,
    hasTriedSubmit,
    canSubmit,
    isAddingInsight,
    isAddingMessage,
    isProcessingResponse,
    isSubmitting: isSubmittingForButton,
    handleAddFormMessage,
    handleAiResponseSubmit,
    handleFormResponseChange,
    handleFormResponseSubmit,
    handleNext,
    handleSubmit,
    handleFormSubmit,
  };
}
